/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { TrackedArray } from 'tracked-built-ins';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { toCamelCase } from '@linc-technologies/linced-common/utils/strings';
import { getDefaultDatatype } from '@linc-technologies/linced-common/utils/dynamic-field-types';
import FieldAccessRoles from '@linc-technologies/linced-common/enums/roles/field-access';
import { alias } from '@ember/object/computed';
import { observes } from '@ember-decorators/object';
import {
  findBy,
  rejectBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class GroupController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  permissions;

  @service
  roles;

  @service
  session;

  @service
  store;

  PersonType = PersonType;
  Scopes = Scopes;
  InputType = InputType;
  FieldAccessRoles = FieldAccessRoles;
  DataType = DataType;

  // inputTypes is a limited list - there are some types used for School Options which we don't want here.
  inputTypes = Object.freeze([
    { value: 1, label: 'Text' },
    { value: 2, label: 'Textarea' },
    { value: 3, label: 'Radio boxes' },
    { value: 4, label: 'Checkbox(es)' },
    { value: 5, label: 'Date' },
    { value: 6, label: 'Email address' },
    { value: 7, label: 'Number' },
    { value: 8, label: 'URL' },
    { value: 9, label: 'Select' },
    { value: 10, label: 'Multiple select (chips)' },
    { value: 11, label: 'Switch' },
    { value: 14, label: 'Jurisdictional ID' },
  ]);

  numericTypes = Object.freeze([
    { value: 3, label: 'Integer (whole numbers)' },
    { value: 4, label: 'Float (decimal numbers)' },
  ]);
  selectDataTypes = Object.freeze([
    { value: 1, label: 'String' },
    { value: 3, label: 'Integer (whole numbers)' },
  ]);

  /*{{!-- enum InputType {
      InputType_Unspecified = 0;
      InputType_Text = 1;
        InputType_Date = 5;
        InputType_Email = 6;
        InputType_Number = 7;
        InputType_Url = 8;
      InputType_Textfield = 2;
      InputType_Radio = 3;
      InputType_Checkbox = 4;
      InputType_Select = 9;
  }

  // DataType is based on basic Go types
  enum DataType {
      DataType_Unspecified = 0;
      DataType_String = 1;
      // List is provided for things like tags, where they can be dynamically added, reordered and deleted.
      DataType_List = 2;
      DataType_Int = 3;
      DataType_Float = 4;
      DataType_Bool = 5;
  } --}}*/

  @tracked
  fieldId = '';

  @tracked
  fieldLabel = '';

  @tracked
  numericType = null;

  @action
  setNumericType(value) {
    this.numericType = value;
  }

  @tracked
  fieldType = null;

  @action
  setFieldType(value) {
    this.fieldType = value;
  }

  @observes('fieldType.value')
  _updateFieldType() {
    let type = this.get('fieldType.value');
    if (!isEmpty(type) && !isEmpty(this.field)) {
      this.field.set('inputType', type);
      if (type === InputType.JURISDICTIONAL_ID) {
        this.field.set('studentPermission', false);
      }
      if (type !== InputType.NUMBER) {
        this.field.set('dataType', getDefaultDatatype(type));
      }
    }
    if (type === InputType.NUMBER) {
      if (!isEmpty(this.field)) {
        switch (this.field.dataType) {
          case DataType.INTEGER:
          case DataType.FLOAT:
            this.numericType = this.getNumericType(this.field.dataType);
            break;
          default:
            this.numericType = this.getNumericType(DataType.INTEGER);
            break;
        }
      } else {
        this.numericType = this.getNumericType(DataType.INTEGER);
      }
    }
  }

  @observes('fieldType.value', 'numericType.value')
  _updateNumericType() {
    if (this.get('fieldType.value') === InputType.NUMBER) {
      let type = this.get('numericType.value');
      if (!isEmpty(type) && !isEmpty(this.field)) {
        this.field.set('dataType', type);
      }
    }
  }

  get fieldTypeHasOptions() {
    let fieldType = this.get('fieldType.value');
    switch (fieldType) {
      case 3:
      case 4:
      case 9:
      case 10:
        return true;
    }

    return false;
  }

  // _updateFieldKey auto-generates a camelCase field key from the label, if the field is new.
  @observes('field.{id,label}')
  _updateFieldKey() {
    if (!this.field.id && !this.field.isDeleted) {
      this.field.set('key', toCamelCase(this.field.label));
    }
  }

  @tracked
  fieldDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteFieldDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  isSortable = false;

  init() {
    super.init(...arguments);
    this.resetForm();
  }

  @alias('roles.canManageStaffPermissions')
  canManagePermissions;

  @tracked
  newOptionLabel;

  @tracked
  newOptionValue;

  @action
  setNewOptionValue(value) {
    this.newOptionValue = value;
  }

  @tracked
  field;

  @tracked
  selectDataType;

  @action
  setSelectDataType(value) {
    this.selectDataType = value;
  }

  @computed('model.personType')
  get filteredRoleOptions() {
    switch (this.model.personType) {
      case PersonType.LEARNER:
        return [
          FieldAccessRoles.STUDENT_MEDICAL,
          FieldAccessRoles.STUDENT_SENSITIVE,
        ];

      case PersonType.STAFF:
        return [
          FieldAccessRoles.STAFF_MEDICAL,
          FieldAccessRoles.STAFF_SENSITIVE,
        ];

      case PersonType.CAREGIVER:
        return [
          FieldAccessRoles.CAREGIVER_MEDICAL,
          FieldAccessRoles.CAREGIVER_SENSITIVE,
        ];
    }
    return [];
  }

  @computed('field.{id,requiredRole}')
  get requiredRoleHasChanged() {
    if (isEmpty(this.field) || isEmpty(this.field.id)) {
      // Not initialised or a new field - no change.
      return false;
    }

    let changed = this.field.changedAttributes();
    if (isEmpty(changed.requiredRole)) {
      // requiredRole is unchanged.
      return false;
    }

    if (isEmpty(changed.requiredRole[0]) && isEmpty(changed.requiredRole[1])) {
      // requiredRole was empty, and is again empty, but may have changed from
      // `undefined` to an empty string.
      return false;
    }

    // requiredRole is flagged as changed, the field already exists, and we
    // haven't determined that it's not a real change - so it *has* changed.
    return true;
  }

  // toggleDialog opens/closes the field dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('fieldDialogOpen');
    if (!this.fieldDialogOpen) {
      this.field.rollbackAttributes();
      this.resetForm();
    }
  }

  @action
  toggleFieldDialog() {
    this.toggleDialog();
  }

  @action
  editField(id) {
    this.store.findRecord('people-schema/field', id).then((field) => {
      let props = {
        field,
      };
      let inputType = field.get('inputType');
      props.fieldType = this.getInputType(inputType);
      if (inputType === InputType.NUMBER) {
        props.numericType = this.getNumericType(field.get('dataType'));
      }
      if (inputType === InputType.SELECT) {
        props.selectDataType = this.selectDataTypes.find(
          (item) => item.value === field.get('dataType'),
        );
      }

      this.setProperties(props);
    });
    this.fieldId = id;
    this.toggleDialog();
  }

  @action
  saveField() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let action = 'create';

    if (isEmpty(this.fieldId)) {
      this.field.set('groupId', this.model);
      this.field.set('schoolId', this.get('session.school'));
      this.field.set('sequence', this.get('model.fields.length') + 1);
    } else {
      action = 'update';
    }

    switch (this.get('fieldType.value')) {
      case InputType.NUMBER:
        this.field.set('dataType', this.get('numericType.value'));
        break;
      case InputType.SELECT:
        this.field.set('dataType', this.selectDataType.value);
        break;
    }
    return this.field
      .save()
      .then(() => {
        this.fieldDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, action);
        this.field.rollbackAttributes();
      })
      .then(() => {
        this.saveInProgress = false;
      });
  }

  @action
  deleteField(id) {
    this.errorMessage = '';
    this.store.findRecord('people-schema/field', id).then((field) => {
      this.fieldLabel = field.get('label');
    });
    this.fieldId = id;
    this.deleteFieldDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let field = this.store.peekRecord('people-schema/field', this.fieldId);

        // Delete record (locally + pushed to server).
        return field
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            field.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteFieldDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  addOption() {
    if (isEmpty(this.get('field.options'))) {
      // Plain array default: @fragmentArray always materializes a real
      // FragmentArray on get() regardless of what was set, so the
      // pushObject/removeObject calls below (on the materialized
      // this.field.options, not this default) keep working unchanged.
      this.field.set('options', []);
    }
    if (this.selectDataType.value === DataType.INTEGER) {
      this.field.options.pushObject({
        valueInt: parseInt(this.newOptionValue, 10),
        label: this.newOptionLabel,
      });
    } else {
      this.field.options.pushObject({
        value: this.newOptionValue,
        label: this.newOptionLabel,
      });
    }
    this.newOptionValue = '';
    this.newOptionLabel = '';
  }

  @action
  removeOption(option) {
    this.field.options.removeObject(option);
  }

  @action
  maybeMutBoth(a, b, context, newValue) {
    if (context.get(a) === context.get(b)) {
      context.set(b, newValue);
    }
    context.set(a, newValue);
  }

  // sortedFields backs the `DragSortList` for `sortDragEnd` below: it must
  // be TrackedArray (not the plain array `sort-by`/`reject-by` produce)
  // since `sortDragEnd` mutates it with native splice - only this one
  // DragSortList reads it (no shared fragment-array handler like
  // linced-admin's enrolment/schema/template.js), so this is safe.
  @computed('model.fields.[]')
  get sortedFields() {
    return new TrackedArray(
      sortBy(
        rejectBy(this.model.fields, 'key', 'nzMOE:botVotingUpdateDate'),
        'sequence',
        'label',
      ),
    );
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      entity.save();
    });
  }

  @action
  optionSortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    // options is a @fragmentArray; .splice() comes from the
    // fragment-array-mutable initializer (StatefulArray only has
    // .replace() natively).
    const [item] = sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
  }

  getInputType(inputType) {
    let types = this.inputTypes;
    return findBy(types, 'value', inputType);
  }

  getNumericType(dataType) {
    let types = this.numericTypes;
    return findBy(types, 'value', dataType);
  }

  handleError(err) {
    // err.errors is Ember Data's AdapterError.errors - a plain array built
    // directly in Ember Data's own source, never Ember-wrapped even before
    // this migration (same trap already fixed elsewhere in this codebase
    // for person-form.js/contact-manager.js).
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors?.[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  resetForm() {
    let field = this.field;
    if (field && field.get('currentState.isDirty')) {
      field.rollbackAttributes();
    }
    this.field = this.store.createRecord('people-schema/field', {
      dataType: getDefaultDatatype(this.inputTypes[0]),
      inputType: this.inputTypes[0].value,
    });
    this.fieldId = '';
    this.fieldType = this.inputTypes[0];
    this.numericType = this.numericTypes[0];
    this.selectDataType = this.selectDataTypes[0];
    this.newOptionLabel = '';
    this.newOptionValue = '';
  }
}
